// Screen page, split by job: Operate (quick offset, estimate history, and
// the display URL — the daily surfaces) and Configure (identity, filters,
// tuning knobs), plus History (its audit trail). Look and copy belong to the
// screen's design template, which has its own editor and its own preview.
//
// Configure is ONE form with ONE save bar — see screen-configure-form.tsx —
// with the danger zone below it. The display URL card is not editable state,
// and its regenerate action is its own destructive confirm.

import { Button, Spinner, Tabs } from "@pej/kore/components";
import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useScreen } from "@/api/screens";
import { useTemplates } from "@/api/templates";
import { EntityHistory } from "@/features/audit/entity-history";
import { AnalyticsCard } from "@/features/screens/analytics-card";
import { DeleteScreenCard } from "@/features/screens/delete-screen-card";
import { DisplayUrlCard } from "@/features/screens/display-url-card";
import { OffsetControl } from "@/features/screens/offset-control";
import { ScreenConfigureForm } from "@/features/screens/screen-configure-form";

export const Route = createFileRoute("/w/$workspaceId/screens/$screenId")({
    // TanStack Router keeps this route's component instance mounted across a
    // $screenId-only navigation (same matched route, only the param
    // changes) — see locations.$locationId.tsx for the fuller writeup of why a
    // hand-rolled `key` prop on the child forms is not a safe substitute.
    // `remountDeps` is the router's own mechanism for forcing a full
    // component remount on a dep change, so every form below re-reads fresh
    // defaultValues whenever the operator navigates from one screen to
    // another.
    remountDeps: ({ params }) => params,
    component: ScreenDetailPage,
});

function ScreenDetailPage() {
    const { workspaceId, screenId } = Route.useParams();
    const screenQuery = useScreen(screenId);
    const templatesQuery = useTemplates(workspaceId);

    if (screenQuery.isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    const screen = screenQuery.data;
    if (!screen) {
        return <p className="text-muted-foreground">This screen couldn't be found.</p>;
    }

    const templates = templatesQuery.data ?? [];

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
                <h1 className="text-xl font-semibold tracking-tight">{screen.name}</h1>
                <Button
                    variant="outline"
                    render={
                        <a href={`${window.location.origin}${screen.displayPath}`} target="_blank" rel="noreferrer" />
                    }
                >
                    <ExternalLink aria-hidden="true" className="size-4" />
                    Open live display
                </Button>
            </div>
            <Tabs defaultValue="operate">
                <Tabs.List>
                    <Tabs.Trigger value="operate">Operate</Tabs.Trigger>
                    <Tabs.Trigger value="configure">Configure</Tabs.Trigger>
                    <Tabs.Trigger value="history">History</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="operate" className="flex flex-col gap-6 pt-4">
                    <OffsetControl screen={screen} />
                    <AnalyticsCard screen={screen} />
                    <DisplayUrlCard screen={screen} />
                </Tabs.Content>
                <Tabs.Content value="configure" className="flex max-w-4xl flex-col gap-4 pt-4">
                    <ScreenConfigureForm workspaceId={workspaceId} screen={screen} templates={templates} />
                    <DeleteScreenCard workspaceId={workspaceId} screen={screen} />
                </Tabs.Content>
                <Tabs.Content value="history" className="pt-4">
                    <EntityHistory targetType="Screen" targetId={screen.id} variant="tab" />
                </Tabs.Content>
            </Tabs>
        </div>
    );
}
